import { AfterViewInit, Component, ElementRef, OnDestroy, ViewChild } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { Deferred, OperationCanceledException } from "../../modules/tcm-core.module";
import { BehaviorSubject, fromEvent, merge, Observable, Subscription } from "rxjs";
import { NotificationViewmodel } from "../../models/notifications/notification-view-model";
import { TcmBaseComponentContext } from "../../modules/tcm-common/services/tcm-base-component-context.service";
import { AuthService } from "../../services";
import { NotificationDataService } from "../../services/notification-data.service";
import { TranslationHelper } from "./notification-helper/translation-helper";
import { debounceTime, distinctUntilChanged, filter, map } from "rxjs/operators";
import { NotificationGetFormRequest } from "../../models/notifications";
import { BroadCastEvents } from "../../common/broad-cast-events.enum";

@Component({
    selector: "notification",
    templateUrl: "./notification.component.html",
    styleUrls: ["./notification.component.scss"],
    standalone: false
})
export class NotificationComponent implements OnDestroy {
  @ViewChild('dropdownBodyContainer') scrollContainer: ElementRef;
  public deleteSubject = new BehaviorSubject<boolean>(false);
  public deleteObs$: Observable<boolean>;
  private showNotificationSubscription: Subscription;
  pageSize: number = 15;
  currentPage: number = 0;
  isNextPageAvailable: boolean = false;
  totalNotificationsCount: number;
  canEdit: boolean;
  isLoading: boolean = false;
  showNotificationIcon: boolean = false;
  notifications: NotificationViewmodel[];
  get notificationCount(): number {
    return this.totalNotificationsCount || 0;
  }
  get isEndOfNotifications(): boolean {
    return !this.isNextPageAvailable && (this.notifications && this.notifications.length > 0);
  }

  get currentUser() {
    return this.authService.currentUserValue;
  }

  private apiEventsSubscription: Subscription;
  private currentUserSubscription: Subscription;
  private scrollSubscription: Subscription;
  private componentCancellationTokenSource = new Deferred();

  constructor(
    protected componentContext: TcmBaseComponentContext,
    protected route: ActivatedRoute,
    private notificationDataService: NotificationDataService,
    private translationHelper: TranslationHelper,
    private readonly authService: AuthService
  ) {
    this.apiEventsSubscription =
      this.componentContext.apiEventSvc.appointmentChangedEvent$.subscribe(
        (x) => this.onAppointmentChangedEvent(x)
      );
    this.componentContext.apiEventSvc.orderStatusChangedEvent$.subscribe(
      (x) => this.notifyNotification()
    );
    this.currentUserSubscription = this.authService.currentUser$.subscribe(
      (x) => {
        this.showNotification();
      }
    );
    this.deleteObs$ = this.deleteSubject.asObservable();
    this.showNotificationSubscription = this.componentContext.broadcastService.getEvents(BroadCastEvents.ShowNotification).subscribe(x => this.showNotification());
  }
  onClick() {
    this.infiniteScroll();
  }

  infiniteScroll() {
    const thisCon = this;
    const container = this.scrollContainer;
    if (container && this.deleteObs$) {
      // Create an observable for scroll events
      const scrollEvent$ = fromEvent(this.scrollContainer.nativeElement, 'scroll');

      const mergedObservable$ = merge(scrollEvent$, this.deleteObs$).pipe(
        debounceTime(500),
        map(() => {
          return this.scrollContainer.nativeElement.scrollHeight - (this.scrollContainer.nativeElement.scrollTop + (.06*this.scrollContainer.nativeElement.scrollHeight)) <= this.scrollContainer.nativeElement.clientHeight
        }),
        filter(atBottom => atBottom && !thisCon.isLoading)
      );

      // Subscribe to the scroll observable to fetch more data
      this.scrollSubscription = mergedObservable$.subscribe(() => { this.showMoreNotificationsOnScroll() });
    }
  }

  async showNotification() {
    if (this.authService.currentUserValue) {
      var userId = this.authService.currentUserValue.id;
      const request = { userId: userId, paging: { currentPage: this.currentPage, pageSize: this.pageSize } } as NotificationGetFormRequest;
      var response = await this.wrapRemoteCallAsync(() =>
        this.notificationDataService.getNotifications(request, this.componentCancellationTokenSource.promise())
      );
      this.notifications = response ? response.notifications : null;
      this.showNotificationIcon = response ? response.showNotification : false;
      this.isNextPageAvailable = response ? response.isNextPageAvailable : false;
      this.totalNotificationsCount = response ? response.totalNotificationsCount : 0;
      if (this.notifications)
        this.notifications.map((x) => {
          x.description = this.translationHelper.getDescription(x);
        });
    }
  }

  private onAppointmentChangedEvent(model) {
    this.notifyNotification();
  }
  ngOnDestroy() {
    this.apiEventsSubscription.unsubscribe();
    this.currentUserSubscription.unsubscribe();
    this.scrollSubscription.unsubscribe();
    this.showNotificationSubscription.unsubscribe();
  }
  resetCount() {
    this.notifications.map((x) => (x.isRead = true));
  }
  async onDelete(
    $event,
    markAll: boolean,
    notification: NotificationViewmodel
  ) {
    $event.stopPropagation();
    var currentUserId = this.authService.currentUserValue.id;
    var response = await this.wrapRemoteCallAsync(() =>
      this.notificationDataService.deleteNotification(
        markAll,
        notification.notificationId,
        currentUserId,
        this.componentCancellationTokenSource.promise()
      )
    );

    if (response) {
      var index = this.notifications.indexOf(notification);
      this.notifications.splice(index, 1);
      this.totalNotificationsCount = notification.isRead ? this.notificationCount : this.totalNotificationsCount - 1;
      this.deleteSubject.next(true);
    }
  }
  async onDeleteAll(markAll: boolean) {
    var currentUserId = this.authService.currentUserValue.id;
    this.notifications = [];
    this.totalNotificationsCount = 0
    var response = await this.wrapRemoteCallAsync(() =>
      this.notificationDataService.deleteNotification(
        markAll,
        0,
        currentUserId,
        this.componentCancellationTokenSource.promise()
      )
    );
  }
  async markAsRead(markAll: boolean, id: number, isRead: boolean) {
    var currentUserId = this.authService.currentUserValue.id;
    if (markAll) {
      this.notifications.map((x) => (x.isRead = true));
      this.totalNotificationsCount = 0;
    } else {
      this.notifications.filter((x) => x.notificationId === id)[0].isRead =
        true;
      this.totalNotificationsCount = isRead ? this.notificationCount : this.totalNotificationsCount - 1;
    }
    if (!isRead) {
      var response = await this.wrapRemoteCallAsync(() =>
        this.notificationDataService.markAsRead(
          markAll,
          id,
          currentUserId,
          this.componentCancellationTokenSource.promise()
        )
      );
    }
  }

  async notifyNotification() {
    if (this.authService.currentUserValue) {
      var userId = this.authService.currentUserValue.id;
      const request = { userId: userId, paging: { currentPage: 0, pageSize: this.pageSize } } as NotificationGetFormRequest;
      var response = await this.wrapRemoteCallAsync(() =>
        this.notificationDataService.getNotifications(
          request,
          this.componentCancellationTokenSource.promise()
        )
      );
      if (response) {
        var newNotifications = response.notifications.filter(x => !this.notifications.some(n => n.notificationId === x.notificationId));
        this.totalNotificationsCount = response.totalNotificationsCount;
        if (newNotifications)
          newNotifications.map((x) => {
            x.description = this.translationHelper.getDescription(x);
          });
        this.notifications.unshift(...newNotifications);
      }
    }
  }
  async showMoreNotificationsOnScroll() {
    this.isLoading = true;
    if (this.authService.currentUserValue) {
      var userId = this.authService.currentUserValue.id;
      if (this.isNextPageAvailable) {
        this.currentPage = this.isNextPageAvailable ? this.currentPage + 1 : this.currentPage;
        const lastUserNotifictaionId = this.notifications[this.notifications.length - 1].userNotificationId;
        const request = { userId: userId, paging: { currentPage: this.currentPage, pageSize: this.pageSize }, lastUserNotifictaionId: lastUserNotifictaionId } as NotificationGetFormRequest;
        var response = await this.wrapRemoteCallAsync(() =>
          this.notificationDataService.getNotifications(
            request,
            this.componentCancellationTokenSource.promise()
          )
        );
        if (response) {
          const newNotifications = response.notifications;
          this.isNextPageAvailable = response.isNextPageAvailable;
          this.totalNotificationsCount = response.totalNotificationsCount;
          if (newNotifications) {
            newNotifications.filter(x => !this.notifications.some(n => n.notificationId === x.notificationId)).map((x) => {
              x.description = this.translationHelper.getDescription(x);
            });
          }
          this.notifications.push(...newNotifications);
        }
      }
    }
    this.isLoading = false;
  }
  private async wrapRemoteCallAsync<T = any>(
    executor: () => Promise<T>,
    raiseError = false
  ): Promise<T> {
    try {
      const result = await executor();

      return result;
    } catch (e) {
      if (!(e instanceof OperationCanceledException)) {
      }

      if (raiseError) {
        throw e;
      } else {
        return null;
      }
    } finally {

    }
  }
}
